// Enhanced table component with pagination, virtual scrolling, and bulk operations
import React, { useState, useMemo, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  filterType?: 'text' | 'select' | 'date' | 'number';
  filterOptions?: Array<{ label: string; value: string }>;
}

export interface EnhancedTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  searchable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  selectable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  serverPagination?: boolean;
  totalRecords?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  virtualScrolling?: boolean;
  loading?: boolean;
  onRowClick?: (row: T, index: number) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  bulkActions?: Array<{
    label: string;
    icon?: React.ReactNode;
    action: (selectedRows: T[]) => void;
    variant?: 'default' | 'destructive';
  }>;
  onExport?: (data: T[]) => void;
  onRefresh?: () => void;
  customActions?: React.ReactNode;
  onDownloadPreview?: (selectedRows: T[]) => void | Promise<void>;
  onDownloadPreviewPdf?: (poIds: string[], selectedRows: T[]) => void | Promise<void>;
  getRowId?: (row: T) => string;
  previewExcludeColumnKeys?: string[];
}

export function EnhancedTable<T extends { id: string }>({
  data,
  columns,
  title,
  searchable = true,
  filterable = true,
  sortable = true,
  selectable = false,
  pagination = true,
  pageSize = 20,
  serverPagination = false,
  totalRecords,
  currentPage: controlledCurrentPage,
  onPageChange,
  virtualScrolling = false,
  loading = false,
  onRowClick,
  onSelectionChange,
  bulkActions = [],
  onExport,
  onRefresh,
  customActions,
  onDownloadPreview,
  onDownloadPreviewPdf,
  getRowId: getRowIdProp,
  previewExcludeColumnKeys = ['actions'],
}: EnhancedTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewSelectedRows, setPreviewSelectedRows] = useState<Set<string>>(new Set());
  const [downloadPreviewLoading, setDownloadPreviewLoading] = useState(false);
  const [downloadPreviewPdfLoading, setDownloadPreviewPdfLoading] = useState(false);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const currentPage = controlledCurrentPage ?? internalCurrentPage;

  const getRowId = useCallback(
    (row: T) => (getRowIdProp ? getRowIdProp(row) : String(row.id)),
    [getRowIdProp]
  );

  // Filter and search data
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search
    if (searchQuery && searchable) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(row =>
        columns.some(col => {
          const value = (row as any)[col.key];
          return value && value.toString().toLowerCase().includes(query);
        })
      );
    }

    // Apply column filters
    if (filterable) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '__all__') {
          filtered = filtered.filter(row => {
            const rowValue = (row as any)[key];
            return rowValue && rowValue.toString().toLowerCase().includes(value.toLowerCase());
          });
        }
      });
    }

    // Apply sorting
    if (sortConfig && sortable) {
      filtered.sort((a, b) => {
        const aValue = (a as any)[sortConfig.key];
        const bValue = (b as any)[sortConfig.key];
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchQuery, filters, sortConfig, searchable, filterable, sortable, columns]);

  // Pagination
  const effectiveTotalRecords = serverPagination
    ? (totalRecords ?? data.length)
    : filteredData.length;
  const totalPages = Math.ceil(effectiveTotalRecords / pageSize);
  const paginatedData = pagination
    ? (serverPagination
      ? filteredData
      : filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize))
    : filteredData;

  const handlePageChange = useCallback((page: number) => {
    if (onPageChange) {
      onPageChange(page);
      return;
    }
    setInternalCurrentPage(page);
  }, [onPageChange]);

  // Selection handlers
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(paginatedData.map(row => row.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [paginatedData]);

  const handleRowSelect = useCallback((rowId: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRows(newSelected);
  }, [selectedRows]);

  // Effect to notify parent of selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      const selected = data.filter(row => selectedRows.has(row.id));
      onSelectionChange(selected);
    }
  }, [selectedRows, data, onSelectionChange]);

  const previewColumns = useMemo(
    () => columns.filter((col) => !previewExcludeColumnKeys.includes(col.key)),
    [columns, previewExcludeColumnKeys]
  );

  const previewTableData = useMemo(() => {
    if (pagination && !serverPagination) {
      return filteredData;
    }
    return paginatedData;
  }, [filteredData, paginatedData, pagination, serverPagination]);

  const handlePreviewSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setPreviewSelectedRows(new Set(previewTableData.map(getRowId)));
    } else {
      setPreviewSelectedRows(new Set());
    }
  }, [previewTableData, getRowId]);

  const handlePreviewRowSelect = useCallback((rowId: string | number, checked: boolean) => {
    const id = String(rowId);
    setPreviewSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleOpenPreviewDialog = () => {
    setPreviewSelectedRows(new Set());
    setPreviewDialogOpen(true);
  };

  const handleConfirmDownloadPreview = async () => {
    const selected = previewTableData.filter((row) => previewSelectedRows.has(getRowId(row)));
    if (!onDownloadPreview || selected.length === 0) return;

    setDownloadPreviewLoading(true);
    try {
      await Promise.resolve(onDownloadPreview(selected));
      setPreviewDialogOpen(false);
      setPreviewSelectedRows(new Set());
    } finally {
      setDownloadPreviewLoading(false);
    }
  };

  const handleConfirmDownloadPreviewPdf = async () => {
    const selected = previewTableData.filter((row) => previewSelectedRows.has(getRowId(row)));
    if (!onDownloadPreviewPdf || selected.length === 0) return;

    const poIds = selected.map(getRowId);

    setDownloadPreviewPdfLoading(true);
    try {
      await Promise.resolve(onDownloadPreviewPdf(poIds, selected));
      setPreviewDialogOpen(false);
      setPreviewSelectedRows(new Set());
    } finally {
      setDownloadPreviewPdfLoading(false);
    }
  };

  const previewDownloadBusy = downloadPreviewLoading || downloadPreviewPdfLoading;

  const allPreviewRowsSelected =
    previewTableData.length > 0 &&
    previewTableData.every((row) => previewSelectedRows.has(getRowId(row)));

  const handleSort = (key: string) => {
    if (!sortable) return;
    
    setSortConfig(current => {
      if (current?.key === key) {
        return current.direction === 'asc' 
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <Card>
      {(title || searchable || filterable || bulkActions.length > 0 || onExport || onRefresh || customActions) && (
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {title && <CardTitle className="text-lg font-semibold">{title}</CardTitle>}
            </div>
            <div className="flex justify-end gap-4 mb-4">
            <Button type="button" onClick={handleOpenPreviewDialog}>
              Download
            </Button>
          </div>
          </div>
          
        </CardHeader>
      )}
 
      <CardContent className="p-0">
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead 
                    key={col.key}
                    className={`${col.sortable && sortable ? 'cursor-pointer hover:bg-muted/50' : ''} ${col.width ? col.width : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <div className="flex items-center gap-2">
                      {col.header}
                      {col.sortable && sortable && sortConfig?.key === col.key && (
                        <span className="text-xs">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="h-24 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Loading...
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="h-24 text-center text-muted-foreground">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow 
                    key={row.id}
                    className={`${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''} ${selectedRows.has(row.id) ? 'bg-muted/30' : ''}`}
                    onClick={() => onRowClick?.(row, index)}
                  >
                    
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        {col.render 
                          ? col.render((row as any)[col.key], row, index)
                          : (row as any)[col.key]
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, effectiveTotalRecords)} of {effectiveTotalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[1400px] max-h-[85vh] flex flex-col bg-white sm:max-w-[1400px]">
          <DialogHeader>
            <DialogTitle>Select items for preview</DialogTitle>
            <DialogDescription>
              Choose the rows to include in the preview download.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allPreviewRowsSelected}
                      onCheckedChange={(checked) => handlePreviewSelectAll(checked === true)}
                      aria-label="Select all"
                    />
                  </TableHead>
                  {previewColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={col.width ? col.width : ''}
                    >
                      {col.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={previewColumns.length + 1} className="h-24 text-center">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : previewTableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={previewColumns.length + 1} className="h-24 text-center text-muted-foreground">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  previewTableData.map((row, index) => (
                    <TableRow
                      key={getRowId(row)}
                      className={previewSelectedRows.has(getRowId(row)) ? 'bg-muted/30' : ''}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={previewSelectedRows.has(getRowId(row))}
                          onCheckedChange={(checked) =>
                            handlePreviewRowSelect(getRowId(row), checked === true)
                          }
                          aria-label={`Select row ${getRowId(row)}`}
                        />
                      </TableCell>
                      {previewColumns.map((col) => (
                        <TableCell key={col.key}>
                          {col.render
                            ? col.render((row as any)[col.key], row, index)
                            : (row as any)[col.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={previewDownloadBusy}
              onClick={() => setPreviewDialogOpen(false)}
            >
              Cancel
            </Button>
            {onDownloadPreviewPdf && (
              <Button
                type="button"
                variant="outline"
                disabled={previewSelectedRows.size === 0 || previewDownloadBusy}
                onClick={() => void handleConfirmDownloadPreviewPdf()}
              >
                {downloadPreviewPdfLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    Download PDF
                    {previewSelectedRows.size > 0 ? ` (${previewSelectedRows.size})` : ''}
                  </>
                )}
              </Button>
            )}
            {onDownloadPreview && (
              <Button
                type="button"
                disabled={previewSelectedRows.size === 0 || previewDownloadBusy}
                onClick={() => void handleConfirmDownloadPreview()}
              >
                {downloadPreviewLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    Download Excel
                    {previewSelectedRows.size > 0 ? ` (${previewSelectedRows.size})` : ''}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}